// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import { useMemo, useEffect, useRef, useCallback } from "react";
import { useKeycloak } from "../context/KeycloakProvider";
import { usePermissions } from "./usePermissions";
import env from "../utils/env";
import {
  getPublicRoutes,
  getProtectedRoutes,
  getAdminRoutes,
  getRoutesByRole,
  getRoutesByPermissions,
  findRouteByPath,
} from "../routes/routeConfig";

export const useRoutes = () => {
  const {
    authenticated,
    getUserInfo,
    syncUserWithDatabase,
    syncedUser,
    syncLoading,
  } = useKeycloak();

  const {
    hasAnyPermission,
    userPermissions,
    loading: permissionsLoading,
    userHasRoles,
  } = usePermissions();

  const hasSynced = useRef(false);
  const syncAttempts = useRef(0);
  const lastSyncUserId = useRef(null);

  const userInfo = useMemo(() => {
    const info = getUserInfo();

    const currentUserId = info?.sub || info?.id;
    if (currentUserId !== lastSyncUserId.current) {
      lastSyncUserId.current = currentUserId;
    }

    return info;
  }, [getUserInfo]);

  const syncUser = useCallback(
    async (userInfoToSync) => {
      if (!userInfoToSync) {
        console.log("Skipping sync - no userInfo");
        return;
      }

      if (syncLoading) {
        console.log("Skipping sync - already syncing");
        return;
      }

      if (
        syncedUser &&
        lastSyncUserId.current === (userInfoToSync.sub || userInfoToSync.id)
      ) {
        console.log("Skipping sync - user already synced");
        return;
      }

      if (syncAttempts.current >= 2) {
        console.log("Skipping sync - maximum attempts reached");
        return;
      }

      try {
        hasSynced.current = true;
        syncAttempts.current += 1;
        await syncUserWithDatabase(userInfoToSync);
      } catch (error) {
        console.error("Error syncing user:", error);
      }
    },
    [syncUserWithDatabase, syncLoading, syncedUser],
  );

  useEffect(() => {
    const shouldSync =
      authenticated &&
      userInfo &&
      !syncedUser &&
      !hasSynced.current &&
      !syncLoading &&
      userHasRoles;
    if (shouldSync) {
      syncUser(userInfo);
    }
  }, [
    authenticated,
    userInfo,
    syncedUser,
    syncUser,
    syncLoading,
    userHasRoles,
  ]);

  useEffect(() => {
    if (!authenticated) {
      console.log("User logged out - resetting sync state");
      hasSynced.current = false;
      syncAttempts.current = 0;
      lastSyncUserId.current = null;
    }
  }, [authenticated]);

  useEffect(() => {
    const currentUserId = userInfo?.sub || userInfo?.id;

    if (
      authenticated &&
      currentUserId &&
      lastSyncUserId.current &&
      lastSyncUserId.current !== currentUserId
    ) {
      console.log("Different user detected - resetting sync state");
      hasSynced.current = false;
      syncAttempts.current = 0;
      syncUser(userInfo);
    }
  }, [userInfo, authenticated, syncUser]);

  const userRoles = useMemo(() => {
    const roles =
      syncedUser?.roles ||
      userInfo?.resourceRoles?.[env.REACT_APP_KEYCLOAK_CLIENT_ID]?.roles ||
      userInfo?.roles ||
      [];
    return Array.isArray(roles) ? roles : [];
  }, [
    syncedUser?.roles,
    userInfo?.resourceRoles?.[env.REACT_APP_KEYCLOAK_CLIENT_ID]?.roles,
    userInfo?.roles,
  ]);

  const publicRoutes = useMemo(() => getPublicRoutes(), []);
  const protectedRoutes = useMemo(() => getProtectedRoutes(), []);
  const adminRoutes = useMemo(() => getAdminRoutes(), []);

  const accessibleRoutes = useMemo(() => {
    if (!authenticated) {
      return [];
    }

    if (permissionsLoading) {
      return [];
    }

    const roleBasedRoutes = getRoutesByRole(userRoles);
    const permissionBasedRoutes = getRoutesByPermissions(userPermissions);

    return roleBasedRoutes.filter((route) => {
      if (!route.permissions) return true;
      return permissionBasedRoutes.includes(route);
    });
  }, [authenticated, userRoles, userPermissions, permissionsLoading]);

  const canAccessRoute = useCallback(
    (routePath) => {
      if (!authenticated) {
        const route = findRouteByPath(routePath);
        return route && route.type === "public";
      }

      if (permissionsLoading) {
        const route = findRouteByPath(routePath);
        return route && route.type === "public";
      }

      const route = findRouteByPath(routePath);
      if (!route) return false;

      if (route.type === "public") return true;

      if (
        route.roles &&
        !route.roles.some((role) => userRoles.includes(role))
      ) {
        return false;
      }

      if (route.permissions && !hasAnyPermission(route.permissions)) {
        return false;
      }

      return true;
    },
    [authenticated, permissionsLoading, userRoles, hasAnyPermission],
  );

  const navigationRoutes = useMemo(() => {
    if (!authenticated) {
      return publicRoutes.filter((route) => !route.redirectIfAuthenticated);
    }

    if (permissionsLoading) {
      return [];
    }

    return accessibleRoutes.filter(
      (route) =>
        route.title && !route.redirect && !route.redirectIfAuthenticated,
    );
  }, [authenticated, permissionsLoading, publicRoutes, accessibleRoutes]);

  const getNavigationRoutes = useCallback(
    () => navigationRoutes,
    [navigationRoutes],
  );

  const getBreadcrumbs = useCallback((currentPath) => {
    const breadcrumbs = [];
    const pathSegments = currentPath.split("/").filter(Boolean);
    let currentFullPath = "";

    pathSegments.forEach((segment, index) => {
      currentFullPath += `/${segment}`;
      const route = findRouteByPath(currentFullPath);
      if (route && route.title) {
        breadcrumbs.push({
          path: currentFullPath,
          title: route.title,
          isLast: index === pathSegments.length - 1,
        });
      }
    });

    return breadcrumbs;
  }, []);

  // Manual resync function for error recovery
  const resyncUser = useCallback(() => {
    if (syncAttempts.current < 2 && userInfo) {
      hasSynced.current = false;
      syncUser(userInfo);
    } else {
      console.warn("Maximum sync attempts reached. Please refresh the page.");
    }
  }, [syncUser, userInfo]);

  return {
    publicRoutes,
    protectedRoutes,
    adminRoutes,
    accessibleRoutes,
    authenticated,
    userRoles,
    userPermissions,
    userInfo,
    permissionsLoading,
    canAccessRoute,
    getNavigationRoutes,
    getBreadcrumbs,
    findRouteByPath,
    resyncUser,
    syncAttempts: syncAttempts.current,
    isSynced: !!syncedUser,
  };
};
